'use client'

import { useState } from 'react'

import { ChangeLanguageForm } from '@src/app/[country]/[lang]/account/settings/_modules/ChangeLanguageForm'
import { ChangeNameForm } from '@src/app/[country]/[lang]/account/settings/_modules/ChangeNameForm'
import { ChangePasswordForm } from '@src/app/[country]/[lang]/account/settings/_modules/ChangePasswordForm'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'

import emailIcon from '../_assets/email.svg'
import languageIcon from '../_assets/language.svg'
import passwordIcon from '../_assets/password.svg'
import privacyIcon from '../_assets/privacy.svg'
import userIcon from '../_assets/user.svg'
import { AccountSettingsItem } from './AccountSettingsItem'

interface Props {
  prefCenterUrl: string
}

export function AccountSettingsContent({ prefCenterUrl }: Props) {
  const { t } = useLocale('accountPage')
  const { t: tc } = useLocale('countries')
  const { t: tl } = useLocale('languages')
  const { user } = useSession()
  const [isNameModalOpen, setNameModalOpen] = useState(false)
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false)
  const [isLanguageModalOpen, setLanguageModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  return (
    <div className="rounded-[30px] bg-bodi-white p-6 lg:p-16">
      <h2 className="text-heading-display-3 mb-8">{t('accountSettings')}</h2>
      <div className="grid grid-flow-row gap-4">
        <AccountSettingsItem
          iconSrc={userIcon}
          iconAlt="user icon"
          ctaText={t('edit')}
          onClick={() => setNameModalOpen(true)}
          data-testid="edit-name"
          title={t('name')}
          description={`${user?.firstName} ${user?.lastName}`}
        />
        <AccountSettingsItem
          iconSrc={emailIcon}
          iconAlt="email icon"
          href={t('emailChangeFaqUrl')}
          target="_blank"
          data-testid="edit-email"
          title={t('email')}
          description={
            <div className="flex flex-col gap-[2px]">
              <div>{user?.email}</div>
              <div>{t('emailChangeHint')}</div>
            </div>
          }
        />
        <AccountSettingsItem
          iconSrc={passwordIcon}
          iconAlt="password icon"
          ctaText={t('edit')}
          onClick={() => setPasswordModalOpen(true)}
          data-testid="edit-password"
          title={t('password')}
          description="••••••••••••"
        />
        <AccountSettingsItem
          iconSrc={languageIcon}
          iconAlt="user icon"
          ctaText={t('edit')}
          onClick={() => setLanguageModalOpen(true)}
          data-testid="edit-language"
          title={t('language')}
          description={tc(user?.userCountry) + ' - ' + tl(user?.userLang)}
        />
        <AccountSettingsItem
          iconSrc={privacyIcon}
          iconAlt="privacy icon"
          href={prefCenterUrl}
          target="_blank"
          data-testid="edit-privacy"
          title={t('pricacyAndPreferences')}
          description={t('updateYourPrivacyPreferences')}
        />
      </div>

      <ChangeNameForm
        isOpen={isNameModalOpen}
        setIsOpen={setNameModalOpen}
        loading={loading}
        setLoading={setLoading}
      />

      <ChangePasswordForm
        isOpen={isPasswordModalOpen}
        setIsOpen={setPasswordModalOpen}
        loading={loading}
        setLoading={setLoading}
      />

      <ChangeLanguageForm
        isOpen={isLanguageModalOpen}
        setIsOpen={setLanguageModalOpen}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  )
}
